import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';

import ProductDetailPanel from '../../ProductDetailPanel';
import PropertyItem from '../PropertyItem';
import InputForm from '../../../../../components/form/InputForm';
import CountOfTotal from '../../../../../components/CountOfTotal';
import useFormPropertyState from './useFormPropertyState';
import { compareProductVersion } from '../../../../../lib/utils';
import { formRequiredRules } from '../../../../../lib/formRules';
import {
  AVAILABLE_CHAIN_SHAPE_TAG_CATEGORY_NAME_LIST,
  MAXIMUM_NUMBER_OF_PRODUCT_SIZE_TEXT,
  MAXIMUM_NUMBER_OF_PRODUCT_WEIGHT_TEXT,
  PRODUCT_CATEGORY_NAME,
  PRODUCT_DETAIL_PAGE_PANEL_NAME,
  PRODUCT_PROPERTY_KEY,
} from '../../../../../constants/service/app/productConstants';
import { MATERIAL_ETC_TAG_NAME } from '../../../../../constants/service/app/tagConstants';
import { REQUIRED_MSG } from '../../../../../constants/service/common/formRulesConstants';
import './ProductProperty.scss';

const PropertyGroup = (props) => {
  const { className, title, children } = props;
  return (
    <div className={cx('property-group', className)}>
      <div className="property-group-title">{title}</div>
      <div className="property-group-content">{children}</div>
    </div>
  );
};

const ProductProperty = (props) => {
  const { isEditable, initialValue, form, productId, propertyList, isCompare, comparisonData, ...res } = props;
  const { productPropertyList: initProductPropertyList } = initialValue;
  const [formProductPropertyList, setFormProductPropertyList] = useFormPropertyState(
    initProductPropertyList,
    propertyList,
    {
      form,
      key: 'productPropertyList',
    },
  );
  const { categoryList, size, weight } = form.getFieldsValue();
  const isShowAcupunctureMaterial = useMemo(() => {
    return !!categoryList.find((item) => item.name === PRODUCT_CATEGORY_NAME.EARRING);
  }, [categoryList]);
  const isShowChainShape = useMemo(() => {
    return !!categoryList.find((item) => AVAILABLE_CHAIN_SHAPE_TAG_CATEGORY_NAME_LIST.includes(item.name));
  }, [categoryList]);
  const [isShowOtherMaterial, setIsShowOtherMaterial] = useState(() => {
    // 기타 소재가 선택 되어있는지 확인
    return !!initProductPropertyList[PRODUCT_PROPERTY_KEY.MAIN_MATERIAL].list.active.find(
      (item) => item.name === MATERIAL_ETC_TAG_NAME,
    );
  });

  useEffect(() => {
    // data fetch 에 따라 initialData 가 달라질 경우
    // 달라진 값에 맞춰 기타 소재 노출 여부 판단
    setIsShowOtherMaterial(
      !!initProductPropertyList[PRODUCT_PROPERTY_KEY.MAIN_MATERIAL].list.active.find(
        (item) => item.name === MATERIAL_ETC_TAG_NAME,
      ),
    );
  }, [initProductPropertyList[PRODUCT_PROPERTY_KEY.MAIN_MATERIAL].list.active]);

  const onChangeCheckBox = ({ key, values }) => {
    if (!Array.isArray(values)) return;
    const tempList = values.map((id) => {
      const target = propertyList[key].list.find((item) => item.id === id);
      return {
        id: target.id,
        name: target.name,
      };
    });

    setFormProductPropertyList({
      ...formProductPropertyList,
      [key]: {
        list: tempList,
        name: propertyList[key].name,
      },
    });

    if (key === PRODUCT_PROPERTY_KEY.MAIN_MATERIAL) {
      setIsShowOtherMaterial(tempList.find((item) => item.name === MATERIAL_ETC_TAG_NAME));
    }
  };

  if (!propertyList) return null;
  const {
    mainMaterial,
    plateMaterial,
    stoneMaterial,
    acupunctureMaterial,
    otherMaterial,
    mainColor,
    decorativeFigure,
    chainShape,
  } = propertyList;

  return (
    <ProductDetailPanel
      name={PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_PROPERTY}
      className="product-property"
      title="상품 속성 정보"
      isCompare={isCompare}
      {...res}
    >
      <div className="panel-body">
        <PropertyGroup title={'소재'}>
          <PropertyItem
            className={cx(
              isCompare &&
                compareProductVersion(
                  initProductPropertyList.mainMaterial,
                  comparisonData.productPropertyList.mainMaterial,
                ),
            )}
            form={form}
            name="mainMaterial"
            isEditable={isEditable}
            title={mainMaterial.name}
            list={mainMaterial.list}
            rules={[formRequiredRules(true, REQUIRED_MSG, false)]}
            productId={productId}
            value={formProductPropertyList.mainMaterial.list}
            confirm={initProductPropertyList.mainMaterial.list}
            rejectInfo={initProductPropertyList.mainMaterial.rejectInfo}
            onChangeCheckBox={onChangeCheckBox}
          />
          {isShowOtherMaterial ? (
            <PropertyItem
              className={cx(
                isCompare &&
                  compareProductVersion(
                    initProductPropertyList.otherMaterial,
                    comparisonData.productPropertyList.otherMaterial,
                  ),
              )}
              form={form}
              name="otherMaterial"
              isEditable={isEditable}
              title={otherMaterial.name}
              list={otherMaterial.list}
              rules={[formRequiredRules(true, REQUIRED_MSG, false)]}
              productId={productId}
              value={formProductPropertyList.otherMaterial.list}
              confirm={initProductPropertyList.otherMaterial.list}
              rejectInfo={initProductPropertyList.otherMaterial.rejectInfo}
              onChangeCheckBox={onChangeCheckBox}
              subContent={
                <div className="sub-info">
                  <div>*주요 소재 중 '기타 소재'를 선택한 경우, 필수로 입력해주세요.</div>
                </div>
              }
            />
          ) : null}
          <PropertyItem
            className={cx(
              isCompare &&
                compareProductVersion(
                  initProductPropertyList.plateMaterial,
                  comparisonData.productPropertyList.plateMaterial,
                ),
            )}
            form={form}
            name="plateMaterial"
            isEditable={isEditable}
            title={plateMaterial.name}
            list={plateMaterial.list}
            productId={productId}
            value={formProductPropertyList.plateMaterial.list}
            confirm={initProductPropertyList.plateMaterial.list}
            rejectInfo={initProductPropertyList.plateMaterial.rejectInfo}
            onChangeCheckBox={onChangeCheckBox}
          />
          <PropertyItem
            className={cx(
              isCompare &&
                compareProductVersion(
                  initProductPropertyList.stoneMaterial,
                  comparisonData.productPropertyList.stoneMaterial,
                ),
            )}
            form={form}
            name="stoneMaterial"
            isEditable={isEditable}
            title={stoneMaterial.name}
            list={stoneMaterial.list}
            productId={productId}
            value={formProductPropertyList.stoneMaterial.list}
            confirm={initProductPropertyList.stoneMaterial.list}
            rejectInfo={initProductPropertyList.stoneMaterial.rejectInfo}
            onChangeCheckBox={onChangeCheckBox}
          />
          {isShowAcupunctureMaterial && (
            <PropertyItem
              className={cx(
                isCompare &&
                  compareProductVersion(
                    initProductPropertyList.acupunctureMaterial,
                    comparisonData.productPropertyList.acupunctureMaterial,
                  ),
              )}
              form={form}
              name="acupunctureMaterial"
              isEditable={isEditable}
              title={acupunctureMaterial.name}
              list={acupunctureMaterial.list}
              rules={[formRequiredRules(true, REQUIRED_MSG, false)]}
              productId={productId}
              value={formProductPropertyList.acupunctureMaterial.list}
              confirm={initProductPropertyList.acupunctureMaterial.list}
              rejectInfo={initProductPropertyList.acupunctureMaterial.rejectInfo}
              onChangeCheckBox={onChangeCheckBox}
            />
          )}
        </PropertyGroup>
        <PropertyGroup title={'컬러'}>
          <PropertyItem
            className={cx(
              isCompare &&
                compareProductVersion(initProductPropertyList.mainColor, comparisonData.productPropertyList.mainColor),
            )}
            form={form}
            name="mainColor"
            isEditable={isEditable}
            title={mainColor.name}
            list={mainColor.list}
            rules={[formRequiredRules(true, REQUIRED_MSG, false)]}
            productId={productId}
            value={formProductPropertyList.mainColor.list}
            confirm={initProductPropertyList.mainColor.list}
            rejectInfo={initProductPropertyList.mainColor.rejectInfo}
            onChangeCheckBox={onChangeCheckBox}
          />
        </PropertyGroup>
        <PropertyGroup title={'모양'}>
          <PropertyItem
            className={cx(
              isCompare &&
                compareProductVersion(
                  initProductPropertyList.decorativeFigure,
                  comparisonData.productPropertyList.decorativeFigure,
                ),
            )}
            form={form}
            name="decorativeFigure"
            isEditable={isEditable}
            title={decorativeFigure.name}
            list={decorativeFigure.list}
            rules={[
              {
                validator: (rule, value, cb) => {
                  if (value.length > 3) {
                    cb('입력 된 장식 모양이 3개를 초과하였습니다.');
                  }
                  cb();
                },
              },
            ]}
            productId={productId}
            value={formProductPropertyList.decorativeFigure.list}
            confirm={initProductPropertyList.decorativeFigure.list}
            rejectInfo={initProductPropertyList.decorativeFigure.rejectInfo}
            subContent={
              <div className="sub-info">
                <div>*장식 모양은 최대 3개까지 등록이 가능합니다.</div>
              </div>
            }
            onChangeCheckBox={onChangeCheckBox}
          />
          {isShowChainShape && (
            <PropertyItem
              className={cx(
                isCompare &&
                  compareProductVersion(
                    initProductPropertyList.chainShape,
                    comparisonData.productPropertyList.chainShape,
                  ),
              )}
              form={form}
              name="chainShape"
              isEditable={isEditable}
              title={chainShape.name}
              list={chainShape.list}
              productId={productId}
              value={formProductPropertyList.chainShape.list}
              confirm={initProductPropertyList.chainShape.list}
              rejectInfo={initProductPropertyList.chainShape.rejectInfo}
              onChangeCheckBox={onChangeCheckBox}
            />
          )}
        </PropertyGroup>
        <InputForm
          form={form}
          label={
            <span>
              사이즈
              <CountOfTotal content={size} maxCnt={MAXIMUM_NUMBER_OF_PRODUCT_SIZE_TEXT} />
            </span>
          }
          name="size"
          className={isCompare && compareProductVersion(initialValue.size, comparisonData.size)}
          rules={[formRequiredRules(true, REQUIRED_MSG)]}
          isEditable={isEditable}
          initialValue={initialValue.size}
          maxLength={MAXIMUM_NUMBER_OF_PRODUCT_SIZE_TEXT}
          placeholder="사이즈로 입력(,로 구분)"
        />
        <InputForm
          form={form}
          label={
            <span>
              중량
              <CountOfTotal content={weight} maxCnt={MAXIMUM_NUMBER_OF_PRODUCT_WEIGHT_TEXT} />
            </span>
          }
          name="weight"
          className={isCompare && compareProductVersion(initialValue.weight, comparisonData.weight)}
          isEditable={isEditable}
          initialValue={initialValue.weight}
          maxLength={MAXIMUM_NUMBER_OF_PRODUCT_WEIGHT_TEXT}
          subContent={
            <div className="sub-info">
              <div>*중량은 g 단위로 기입해주세요.</div>
            </div>
          }
        />
      </div>
      <div className="hidden-form">
        <InputForm form={form} type="hidden" name="productPropertyList" initialValue={formProductPropertyList} />
      </div>
    </ProductDetailPanel>
  );
};

export default ProductProperty;
